// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import { THForm } from '@Molecules/THForm/THForm';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { SharingRule, SharingRuleValues } from 'thehive-sdk';

export interface OrgValuesForm {
    name: string;
    description: string;
    taskRule?: SharingRule;
    observableRule?: SharingRule;
    avatar?: string;
}

interface OrgFormProps {
    form: FormInstance;
    isValid: boolean;
    onFinish(values: OrgValuesForm): void;
    onChange: () => void;
    onCancel: () => void;
}

export const OrgForm = ({ form, isValid, onFinish, onChange, onCancel }: OrgFormProps): ReactElement => {
    const i = useLocalMessage();

    return (
        <THForm<OrgValuesForm>
            name="org-form"
            onFinish={onFinish}
            form={form}
            onValuesChange={onChange}
            initialValues={{
                taskRule: SharingRuleValues[0],
                observableRule: SharingRuleValues[0],
            }}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} footerText={i('general.confirm')} />}
            >
                <THForm.Item
                    label={i('organisation.create.form.name.label')}
                    name="name"
                    rules={[
                        {
                            required: true,
                            message: i('organisation.create.form.name.required'),
                            max: 64,
                            pattern: /^[\w\- ]+$/g,
                        },
                    ]}
                >
                    <THInput placeholder={i('organisation.create.form.name.placeholder')} />
                </THForm.Item>

                <THForm.Item
                    label={i('organisation.create.form.description.label')}
                    name="description"
                    rules={[
                        {
                            required: true,
                            message: i('organisation.create.form.description.required'),
                        },
                    ]}
                >
                    <THTextArea placeholder={i('organisation.create.form.description.placeholder')} />
                </THForm.Item>

                <THForm.Item name="taskRule" label={i('organisation.create.form.task-rule.label')}>
                    <THSelect
                        options={SharingRuleValues.map((value) => ({ label: value, value }))}
                        placeholder={i('organisation.create.form.task-rule.placeholder')}
                    />
                </THForm.Item>

                <THForm.Item name="observableRule" label={i('organisation.create.form.observable-rule.label')}>
                    <THSelect
                        options={SharingRuleValues.map((value) => ({ label: value, value }))}
                        placeholder={i('organisation.create.form.observable-rule.placeholder')}
                    />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
